<template>
  <div class="app-page">
    <Header ref="header" helpURL="models/">
      <template #header-slot>
        <BreadcrumbNavigation />
      </template>
      <template #page-slot>
        <BreadcrumbSelectProjectAndDataset
          @project-changed="handleProjectChanged"
        />
      </template>
    </Header>
    <div class="main-page-div">
      <main class="main-display-area">
        <router-view />
      </main>
    </div>
  </div>
  <TasksModal />
</template>

<script>
import Header from '@/components/Header.vue';
import DatastoreConnect from '@/assets/js/DatastoreFunctions/datastore-interface';
import BreadcrumbNavigation from '@/components/BreadcrumbNavigation.vue';
import TasksModal from '@/components/TasksStatusComponent/TasksModalV2.vue';
import BreadcrumbSelectProjectAndDataset from '@/components/BreadcrumbSelectProjectAndDataset.vue';
import { ref, computed, provide } from 'vue';
import { useToast } from 'vue-toast-notification';
import 'vue-toast-notification/dist/theme-sugar.css';

const $toast = useToast();

export default {
  components: {
    Header,
    BreadcrumbNavigation,
    TasksModal,
    BreadcrumbSelectProjectAndDataset,
  },
  setup() {
    const experiments = ref(null);
    provide('experiments', computed(() => experiments.value));
    return { experiments };
  },
  data() {
    return {
      dataConnect: new DatastoreConnect(),
    };
  },
  computed: {
    currentProject: {
      get() {
        return this.$store.state.projects.currentProject;
      },
      set(value) {
        this.$store.commit('projects/setCurrentProject', value);
      },
    },
    currentExperiment: {
      get() {
        return this.$store.state.experiments.currentExperiment;
      },
      set(exp) {
        this.$store.commit('experiments/setCurrentExperiment', exp);
      },
    },
    currentSession: {
      get() {
        return this.$store.state.experiments.currentSession;
      },
      set(session) {
        this.$store.commit('experiments/setCurrentSession', session);
      },
    },
  },
  async beforeMount() {
    if (this.$route.query.exp_id) {
      const experiment_id = parseInt(this.$route.query.exp_id);
      const experiment = await this.getExperiment(experiment_id);
      if (experiment) {
        this.currentExperiment = experiment;
      }
    } else {
      this.currentExperiment = null;
    }
    if (this.$route.query.train_session_id) {
      const train_session_id = parseInt(this.$route.query.train_session_id);
      const trainingSession = await this.getTrainingSession(train_session_id);
      if (trainingSession) {
        this.currentSession = trainingSession;
      }

      if (this.currentSession && !this.currentExperiment) {
        const experiment = await this.getExperiment(this.currentSession.trainer_id);
        if (experiment) {
          this.currentExperiment = experiment;
        }
      }
    } else {
      this.currentSession = null;
    }
  },
  methods: {
    async handleProjectChanged() {
      this.$router.push({
        name: 'experiment.list',
        query: {},
        params: { projectId: this.currentProject?.id },
      });
      this.experiments = await this.getExperiments();
    },
    async getExperiments() {
      if (this.currentProject) {
        const resp = await this.dataConnect.getExperimentList({ project_id: this.currentProject.id })
          .catch((error) => {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to retrieve experiments: ${error}`,
              type: "error",
              position: "top",
            });
          });
        if (!resp || resp.error || !resp.result) {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve experiments: ${resp.error.message}`,
            type: "error",
            position: "top",
          });
          return [];
        }
        return resp.result;
      }
      return [];
    },
    async getExperiment(id) {
      return this.dataConnect.getExperiment({ trainer_id: id })
        .then(async (data) => {
          if (!data.result) {
            throw data.error.message;
          }
          return data.result;
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve experiment: ${error}`,
            type: "error",
            position: "top",
          });
        });
    },
    async getTrainingSession(training_session_id) {
      return this.dataConnect.getTrainingSession({ trainer_session_id: training_session_id })
        .then(async (data) => {
          if (!data.result) {
            throw data.error.message;
          }

          const date1 = new Date(data.result.deleted);
          const date2 = new Date("0001-01-01T00:00:00Z");
          if (date1 > date2) {
            $toast.open({
              queue: true,
              dismissible: true,
              message: `Failed to retrieve training session: Session has been removed`,
              type: "error",
              position: "top",
            });
            return null;
          }
          return data.result;
        })
        .catch((error) => {
          $toast.open({
            queue: true,
            dismissible: true,
            message: `Failed to retrieve training session: ${error}`,
            type: "error",
            position: "top",
          });
        });
    },
  },
};
</script>

<style lang="scss" scoped>

.main {
  height: 100vh;
}
</style>
